import { MAP_BUILD_WAIT_SECONDS } from './mapBuildWait';

/** Taille d'un lot Macro / V1 / V2 (force generate + génération auto). */
export const FORCE_MAP_BATCH_SIZE = 3;

export type MappingGenerationItem = {
    _id?: string;
    id?: string;
    name?: string;
    version?: string;
    is_finish?: boolean;
    created_at?: string | Date | null;
};

export type SidebarDiagram = {
    id: string;
    name: string;
    version: string;
};

export type MappingGenerationProgress = {
    ready: number;
    total: number;
    remaining: number;
};

function asList(swimlanes: MappingGenerationItem[] | null | undefined): MappingGenerationItem[] {
    return Array.isArray(swimlanes) ? swimlanes : [];
}

function mappingId(item: MappingGenerationItem, index: number): string {
    return String(item._id || item.id || `diagram-${index}`);
}

function parseCreatedAtMs(value: MappingGenerationItem['created_at']): number | null {
    if (!value) return null;
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isNaN(ms) ? null : ms;
    }
    const ms = Date.parse(String(value));
    return Number.isNaN(ms) ? null : ms;
}

function isPendingItem(item: MappingGenerationItem): boolean {
    return item.is_finish === false;
}

/** Pending dont la fenêtre 10 min est dépassée (ou sans created_at → stale). */
export function isStalePendingMapping(
    item: MappingGenerationItem,
    nowMs: number = Date.now(),
): boolean {
    if (!isPendingItem(item)) return false;
    const createdAt = parseCreatedAtMs(item.created_at);
    if (createdAt == null) return true;
    const elapsedSec = Math.floor((nowMs - createdAt) / 1000);
    return elapsedSec >= MAP_BUILD_WAIT_SECONDS;
}

export function getActivePendingMappings(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): MappingGenerationItem[] {
    return asList(swimlanes).filter(
        (item) => isPendingItem(item) && !isStalePendingMapping(item, nowMs),
    );
}

export function hasActivePendingMappings(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    return getActivePendingMappings(swimlanes, nowMs).length > 0;
}

export function hasStalePendingMappings(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    return asList(swimlanes).some((item) => isStalePendingMapping(item, nowMs));
}

/** True s'il reste des pending (actifs ou stale). Conservé pour les checks "pas encore tous ready". */
export function hasPendingMappings(swimlanes: MappingGenerationItem[] | null | undefined): boolean {
    return asList(swimlanes).some((item) => isPendingItem(item));
}

export function areAllMappingsReady(swimlanes: MappingGenerationItem[] | null | undefined): boolean {
    const list = asList(swimlanes);
    return list.length > 0 && !hasPendingMappings(list);
}

/**
 * Cartos utilisables prêtes : au moins une finished, et aucun pending encore dans la fenêtre 10 min.
 * Les pending périmés (stale) ne bloquent pas — la gen peut avoir partiellement abouti.
 */
export function isMappingDiscoveryComplete(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    return getSidebarDiagrams(swimlanes).length > 0 && !hasActivePendingMappings(swimlanes, nowMs);
}

/**
 * Afficher le message "prend plus de temps" seulement s'il n'y a encore aucune carto prête.
 * S'il reste des pending stale mais des Macro/V1 déjà listées → pas d'alarme.
 */
export function shouldShowMappingGenerationTimedOut(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): boolean {
    if (hasActivePendingMappings(swimlanes, nowMs)) return false;
    if (!hasStalePendingMappings(swimlanes, nowMs)) return false;
    return getSidebarDiagrams(swimlanes).length === 0;
}

export function isAwaitingFirstBatch(
    swimlanes: MappingGenerationItem[] | null | undefined,
    allInterviewsCompleted: boolean,
): boolean {
    return asList(swimlanes).length === 0 && Boolean(allInterviewsCompleted);
}

/**
 * Progression du lot en cours (pending actifs), pas du catalogue entier.
 * Ex. 6 ready + 2 actifs restants → remaining 2, total 3, ready 1.
 */
export function getMappingGenerationProgress(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): MappingGenerationProgress {
    const activePending = getActivePendingMappings(swimlanes, nowMs);
    if (activePending.length === 0) {
        const list = asList(swimlanes);
        const finished = list.filter((item) => !isPendingItem(item)).length;
        return { ready: finished, total: list.length, remaining: 0 };
    }
    const total = Math.max(FORCE_MAP_BATCH_SIZE, activePending.length);
    const remaining = activePending.length;
    const ready = total - remaining;
    return { ready, total, remaining };
}

/** Cartos prêtes uniquement — visibles même pendant un lot actif. */
export function getSidebarDiagrams(
    swimlanes: MappingGenerationItem[] | null | undefined,
): SidebarDiagram[] {
    const list = asList(swimlanes);
    return list
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !isPendingItem(item))
        .map(({ item, index }) => ({
            id: mappingId(item, index),
            name: item.name || `Diagram ${index + 1}`,
            version: item.version || 'v1',
        }));
}

/** Temps restant (s) sur 10 min, basé sur created_at du plus ancien pending **actif**. */
export function getMappingGenCountdownSeconds(
    swimlanes: MappingGenerationItem[] | null | undefined,
    nowMs: number = Date.now(),
): number | null {
    const pending = getActivePendingMappings(swimlanes, nowMs);
    if (pending.length === 0) return null;

    const timestamps = pending
        .map((item) => parseCreatedAtMs(item.created_at))
        .filter((ms): ms is number => ms != null);

    if (timestamps.length === 0) return MAP_BUILD_WAIT_SECONDS;

    const oldest = Math.min(...timestamps);
    const elapsed = Math.floor((nowMs - oldest) / 1000);
    return Math.max(0, MAP_BUILD_WAIT_SECONDS - elapsed);
}
